import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AgreementModal from "../components/AgreementModal";
import Cookies from "js-cookie";

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 50,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeInOut",
      duration: 0.6, // 애니메이션 시간 단축
    },
  },
  exit: {
    opacity: 0,
    y: -50,
    transition: {
      duration: 0.4, // 애니메이션 시간 단축
    },
  },
};

function BondAgreementPage() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const [userInfo, setUserInfo] = useState(null);

  const [agreements, setAgreements] = useState({
    agreement1: false,
    agreement2: false,
    agreement3: false,
    agreement4: false,
  });

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAgreementId, setCurrentAgreementId] = useState(null);
  const [modalContent, setModalContent] = useState({
    title: "",
    content: null,
  });

  // 쿠키에서 사용자 정보 가져오기
  useEffect(() => {
    try {
      // 쿠키 정보가 있다면 파싱
      const userInfoFromCookie = Cookies.get("USER_INFO");
      if (userInfoFromCookie) {
        const parsedUserInfo = JSON.parse(userInfoFromCookie);
        setUserInfo(parsedUserInfo);
      }
    } catch (error) {}
  }, []);

  // 모달 내용 정의
  const agreementContents = {
    agreement1: {
      title: "하자보수 및 손해배상청구권 양도",
      content: (
        <div>
          <p>
            1. "갑"은 하자보수의무자(시행사, 시공사, 보증사 등 사업주체)에
            대하여 가지고 있는 하자보수청구 및 하자보수에 갈음하는
            손해배상금(이자 및 지연손해금 포함)청구권을 "을"에게 양도하기로 하고
            이에 본 양도증서에 서명날인한다.
          </p>
        </div>
      ),
    },
    agreement2: {
      title: "채권 행사 및 소송 권한 위임",
      content: (
        <div>
          <p>
            2. "갑"은 위 하자보수의무자들을 상대로 한 위 손해배상채권의 양도통지
            및 손해배상채권행사 등 하자소송에 관한 일체의 권한(단, 준공전 하자를
            포함한 합의종결권한은 제외함. 만일, 준공전 하자를 포함하여
            합의종결을 할 경우, "을"은 개별 구분소유자로부터 다시 동의를 받아야
            하고 전체 85% 이상의 동의요건을 충족하여야만 합의종결이 가능함)을
            "을"에게 위임한다.
          </p>
        </div>
      ),
    },
    agreement3: {
      title: "승소금(합의금 포함) 사용 방법",
      content: (
        <div>
          <p>
            3. 승소판결원리금(합의금 포함)의 사용 및 분배방법은 "을"의 의결에
            따르되, ㉮ 계약에 의한 변호사 보수 및 소송대납비용은 위 승소금에서
            우선공제・정산하고, ㉯ "㉮"항의 비용을 제한 나머지 금액 중 전유부분
            판결금은 각 세대(구분소유자)에 지급하고, 공용부분 판결금(안전관리비,
            소송비용액확정결정에 따른 회수금 포함)은 입주자대표회의의 의결에
            따라 하자보수를 위한 공사비용으로 사용함에 "갑"은 동의한다.
          </p>
        </div>
      ),
    },
    agreement4: {
      title: "동의 및 철회 불가 확인",
      content: (
        <div>
          <p>
            4. "갑"은 본 동의(계약)가 정상적인 의사와 권한으로
            성립(체결)되었음을 확인하고, 동의의사를 철회하지 않기로 하며, "을"이
            "갑"을 위하여 하자보수청구 및 하자담보추급권 행사(하자소송 포함)등
            하자관련 업무를 진행함에 있어 개인정보수집·이용·제공에 동의한다.
          </p>
        </div>
      ),
    },
  };

  // 부드러운 스크롤 함수
  const scrollToConfirmButton = () => {
    if (confirmButtonRef.current) {
      confirmButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // 모든 필수 항목이 체크되었는지 확인
  useEffect(() => {
    const allRequired =
      agreements.agreement1 &&
      agreements.agreement2 &&
      agreements.agreement3 &&
      agreements.agreement4;

    setIsButtonEnabled(allRequired);

    // 이미 모든 약관에 동의한 상태에서 useEffect가 실행된 경우는 스크롤하지 않음
    // 모달 동의 버튼 클릭 시 handleAgree에서 스크롤을 처리함
  }, [
    agreements.agreement1,
    agreements.agreement2,
    agreements.agreement3,
    agreements.agreement4,
  ]);

  // 확인 버튼 클릭 처리
  const handleConfirmClick = () => {
    if (isButtonEnabled) {
      // 다음 페이지로 이동
      navigate("/lawyer-agreement");
    }
  };

  // 동의 항목 클릭 처리 - 어떤 영역을 클릭하더라도 모달이 열림
  const handleAgreementItemClick = (e, id) => {
    // 모달 열기
    if (agreementContents[id]) {
      setCurrentAgreementId(id);
      setModalContent({
        title: agreementContents[id].title,
        content: agreementContents[id].content,
      });
      setModalOpen(true);
    }
  };

  // 모달에서 동의 버튼 클릭 처리
  const handleAgree = (agreementId) => {
    setAgreements((prev) => ({
      ...prev,
      [agreementId]: true,
    }));

    // 모달 닫기
    setModalOpen(false);

    // 모든 약관에 동의했는지 확인 후 스크롤
    const updatedAgreements = {
      ...agreements,
      [agreementId]: true,
    };

    // 모든 약관에 동의했는지 확인
    const allAgreed = Object.values(updatedAgreements).every(
      (value) => value === true
    );

    if (allAgreed) {
      // 약간의 딜레이를 주어 모달이 완전히 닫힌 후 스크롤
      setTimeout(() => {
        scrollToConfirmButton();
      }, 0);
    }
  };

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      ref={pageRef}
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <h1>채권양도·양수 동의 (1/3)</h1>
      </motion.div>

      <motion.div
        className="logo-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <img src="/mainLogo.png" alt="메인 로고" className="main-logo" />
      </motion.div>

      <motion.div
        className="agreement-content"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h2>약관에 동의해 주세요</h2>
        <p>채권 양도 양수에 필요한 사항을 확인해 주세요.</p>

        <div className="agreement-items">
          {Object.keys(agreementContents).map((id, index) => (
            <motion.div
              key={id}
              className="agreement-item"
              onClick={(e) => handleAgreementItemClick(e, id)}
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
              whileHover={{ scale: 1.01 }}
            >
              <div className="checkbox-image">
                <img
                  src={agreements[id] ? "/checked.png" : "/checkedInActive.png"}
                  alt="체크박스"
                />
              </div>
              <label>
                {agreementContents[id].title}
                <span className="required">[필수]</span>
              </label>
              <div className="arrow-icon">›</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.button
        className="confirm-button"
        disabled={!isButtonEnabled}
        onClick={handleConfirmClick}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        whileHover={isButtonEnabled ? { scale: 1.02 } : {}}
        whileTap={isButtonEnabled ? { scale: 0.98 } : {}}
        ref={confirmButtonRef}
      >
        확인하기
      </motion.button>

      <AgreementModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        content={modalContent.content}
        agreementId={currentAgreementId}
        onAgree={handleAgree}
        userInfo={userInfo}
      />
    </motion.div>
  );
}

export default BondAgreementPage;
